import { ref, computed } from 'vue';
import store from '@/store';

/**
 * Lookup methods that fetch auxiliary data the form needs to render selects
 * (clubes, divisiones, recintos, tipos, tags, eventos). Extracted from
 * useEventForm.js to keep that file under the canonical ~200 LOC ceiling.
 *
 * Ownership: this composable owns the load lifecycle of the lookups it
 * fetches. It exposes an aggregated `loading`/`error` state so the form can
 * distinguish "still loading", "failed" and "loaded empty" instead of
 * rendering empty selects with no explanation.
 *
 * Contract: a failing lookup is RECORDED in `errors` and the rejection is still
 * propagated to the caller (the pre-existing contract — callers rely on it). The
 * two optional aids (`getTags`, `getEventos`) are invoked fire-and-forget from
 * watchers, so they absorb the rejection for their call site while leaving the
 * failure recorded; that is the difference between "silently swallowed" and
 * "degraded, but observable". `loadAll()` reports the aggregate outcome.
 */
export function useEventFormLookups() {
    const clubes = ref([]);
    const divisiones = ref([]);
    const tipos = ref([]);
    const recintos = ref([]);
    const tags = ref([]);
    const eventos = ref([]);

    // Per-lookup pending flags, so a failing lookup does not mark a succeeding
    // one as still loading. `loading` is the aggregate the UI consumes.
    const pending = ref(0);
    const loading = computed(() => pending.value > 0);
    const eventosPending = ref(0);
    const eventosLoading = computed(() => eventosPending.value > 0);

    // Keyed by lookup name (clubes, divisiones, ...). Empty object means all OK.
    const errors = ref({});
    const hasErrors = computed(() => Object.keys(errors.value).length > 0);

    function clearError(name) {
        if (!errors.value[name]) {
            return;
        }
        const next = { ...errors.value };
        delete next[name];
        errors.value = next;
    }

    /**
     * Runs one lookup while owning its lifecycle accounting. Records the failure
     * in `errors` and rethrows, preserving the caller-visible contract.
     *
     * @param {string} name  Lookup key used in `errors`.
     * @param {Function} fetcher  Resolves with the fetched value; may reject.
     * @returns {Promise<*>} The fetched value (rejects if the lookup fails).
     */
    async function track(name, fetcher) {
        pending.value += 1;

        try {
            const value = await fetcher();

            // Clear a previous failure so a retry can recover.
            clearError(name);

            return value;
        } catch (error) {
            errors.value = { ...errors.value, [name]: error };
            throw error;
        } finally {
            pending.value -= 1;
        }
    }

    function getClubes() {
        return track('clubes', () =>
            store.dispatch(store.getters.getConfig?.getVuexAlias?.('select.clubes') || 'SELECT_CLUBES').then(res => {
                clubes.value = res.data;
            })
        );
    }

    function getDivisiones() {
        return track('divisiones', () =>
            store.dispatch(store.getters.getConfig?.getVuexAlias?.('select.divisiones') || 'SELECT_DIVISIONES').then(res => {
                divisiones.value = res.data;
            })
        );
    }

    function getRecintos() {
        return track('recintos', () =>
            store.dispatch(store.getters.getConfig?.getVuexAlias?.('recintos.index') || 'RECINTOS_INDEX').then(res => {
                recintos.value = res.data.data;
            })
        );
    }

    function getTipos(listaAbonadosId) {
        const params = {
            filters: {
                per_page: 9999,
                page: 1,
                search: '',
                all: 1
            },
            lista_id: listaAbonadosId ?? store.getters.getListaActiva?.id
        };
        return track('tipos', () =>
            store.dispatch(store.getters.getConfig?.getVuexAlias?.('back.precios.index') || 'BACK_PRECIOS_INDEX', params).then(res => {
                tipos.value = res.data;
            })
        );
    }

    // `tags` and `eventos` are optional aids (tag picker, "compartir entradas"
    // list). Se invocan fire-and-forget desde watchers, así que absorben el
    // rechazo para no generar un unhandled rejection — pero el fallo queda
    // registrado en `errors` (antes se descartaba en silencio con un `.catch()`
    // vacío, sin forma de distinguir "vacío" de "falló").
    function getTags() {
        const params = {
            filters: { to_select: true }
        };
        return track('tags', () =>
            store.dispatch(store.getters.getConfig?.getVuexAlias?.('back.tags') || 'BACK_TAGS', params).then(res => {
                tags.value = res.data.data;
            })
        ).catch(() => {
            tags.value = [];
        });
    }

    function getEventos(fechaMaxima) {
        const params = {
            filters: {
                to_select: true,
                para_compartir: true,
                fecha_maxima: fechaMaxima
            }
        };
        eventosPending.value += 1;
        return track('eventos', () =>
            store.dispatch(store.getters.getConfig?.getVuexAlias?.('back.events.index') || 'BACK_EVENTS_INDEX', params).then(res => {
                eventos.value = res.data.data;
            })
        ).catch(() => {
            eventos.value = [];
        }).finally(() => {
            eventosPending.value -= 1;
        });
    }

    /**
     * Lookups `loadAll()` fetches eagerly, because the CREATE/EDIT form cannot
     * render without them.
     */
    const EAGER_LOOKUPS = Object.freeze(['clubes', 'divisiones', 'recintos', 'tipos', 'tags']);

    /**
     * Lookups fetched on demand from watchers (the "compartir entradas" tag
     * picker, the event picker). They own their own lifecycle: the form never
     * waits on them, so they must NOT be part of `loadAll()`'s aggregate.
     *
     * Naming both scopes explicitly is the point: before this, `errors` included
     * `eventos` while `loadAll()`'s `ok` list did not, so the same public state
     * had two different definitions of "every lookup" and a caller could read a
     * failed `eventos` as "all lookups loaded".
     */
    const LAZY_LOOKUPS = Object.freeze(['eventos']);

    /**
     * Loads every EAGER lookup and reports that scope's aggregate outcome. Does
     * not throw: the caller decides whether a failed lookup blocks or degrades
     * (the form renders selects, so it degrades and surfaces the error).
     *
     * `failed` and `ok` partition exactly `EAGER_LOOKUPS`. Lazy lookups are
     * deliberately out of scope here — their failures stay visible through
     * `lookupsErrors`/`lookupsHasErrors` and are listed in `lazyFailed`.
     *
     * @param {Object} [options]
     * @param {number|string} [options.listaAbonadosId]  Lista for `getTipos`.
     * @returns {Promise<{failed: string[], ok: string[], lazyFailed: string[]}>}
     */
    async function loadAll({ listaAbonadosId } = {}) {
        await Promise.allSettled([
            getClubes(),
            getDivisiones(),
            getRecintos(),
            getTipos(listaAbonadosId),
            getTags()
        ]);

        const failedNames = Object.keys(errors.value);

        return {
            failed: EAGER_LOOKUPS.filter(name => failedNames.includes(name)),
            ok: EAGER_LOOKUPS.filter(name => !failedNames.includes(name)),
            // Any lazy lookup that failed stays observable without inflating the
            // eager aggregate. Add a retry path when a caller needs one.
            lazyFailed: LAZY_LOOKUPS.filter(name => failedNames.includes(name))
        };
    }

    return {
        // Lookup data
        clubes,
        divisiones,
        tipos,
        recintos,
        tags,
        eventos,
        eventosLoading,
        // Lifecycle
        lookupsLoading: loading,
        lookupsErrors: errors,
        lookupsHasErrors: hasErrors,
        loadAll,
        // Methods
        getClubes,
        getDivisiones,
        getRecintos,
        getTipos,
        getTags,
        getEventos
    };
}